import NumberInput from '../../../components/NumberInput';
import { useEffect, useRef, useState } from 'react';

import FrameSection from '../../../components/dnd/FrameSection';
import TableInventory from '../../../components/dnd/inventory/TableInventory';
import { useAuth } from '../../../hooks/useAuth';
import Weight, { WeightUnit } from '../../../components/dnd/inventory/Weight';
import { CurrencyCode } from '../../../components/dnd/inventory/Currency';
import Currencies from '../../../components/dnd/inventory/Currencies';
import CurrencyText from '../../../components/dnd/inventory/CurrencyText';

type CurrencyAmounts = Record<CurrencyCode, number>;
const emptyValue: CurrencyAmounts = { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 };

type TreasureReference = {
    id: number;
    treasure_id: null;
    name: string;
    quantity: number;
    weight: number;
    notes: string | null;
    value: CurrencyAmounts;
};

type PendingTreasure = Omit<TreasureReference, 'id'> & { id: string };
type TreasureItem = TreasureReference | PendingTreasure;

type TreasureProps = {
    characterId: number;
    currency: CurrencyAmounts;
    items: TreasureReference[];
    onWeightChange?: (weight: number) => void;
    weightUnit: WeightUnit;
};


function Treasure({ characterId, currency, items, onWeightChange, weightUnit }: TreasureProps) {
    const { apiRequest } = useAuth();
    const [amounts, setAmounts] = useState(currency);
    const [treasures, setTreasures] = useState<TreasureItem[]>(items.map((item) => ({ ...item, value: item.value || emptyValue })));
    const [error, setError] = useState('');
    const nextPendingId = useRef(1);
    const totalCoinWeight = Object.values(amounts).reduce((total, amount) => total + amount, 0) * 0.02;

    useEffect(() => setAmounts(currency), [currency]);
    useEffect(() => setTreasures(items.map((item) => ({ ...item, value: item.value || emptyValue }))), [items]);

    useEffect(() => {
        const customWeight = treasures.reduce((total, item) => total + item.weight * item.quantity, 0);
        onWeightChange?.(totalCoinWeight + customWeight);
    }, [totalCoinWeight, treasures, onWeightChange]);

    const saveCurrency = async (nextAmounts: CurrencyAmounts) => {
        try {
            const result = await apiRequest<{ success: boolean; data: CurrencyAmounts }>(`/api/characters/${characterId}/currency`, 'PUT', nextAmounts);
            setAmounts(result.data);
            setError('');
        } catch {
            setError('Could not save currency amounts.');
        }
    };

    const saveTreasure = async (treasure: TreasureItem) => {
        try {
            if (typeof treasure.id === 'string') {
                const result = await apiRequest<{ success: boolean; data: TreasureReference }>(`/api/characters/${characterId}/treasures`, 'POST', treasure);
                setTreasures((current) => current.map((item) => item.id === treasure.id ? result.data : item));
            } else {
                await apiRequest(`/api/characters/${characterId}/treasures/${treasure.id}`, 'PUT', treasure);
            }
            setError('');
            return true;
        } catch {
            setError('Could not save custom treasure changes.');
            return false;
        }
    };

    const deleteTreasure = async (treasure: TreasureItem) => {
        if (typeof treasure.id === 'string') return true;
        try {
            await apiRequest(`/api/characters/${characterId}/treasures/${treasure.id}`, 'DELETE');
            setError('');
            return true;
        } catch {
            setError('Could not delete the custom treasure.');
            return false;
        }
    };

    return (
        <>
            <FrameSection title="Treasure" />
            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <Currencies
                cp={amounts.cp}
                sp={amounts.sp}
                ep={amounts.ep}
                gp={amounts.gp}
                pp={amounts.pp}
                onChange={(currency, amount) => setAmounts((current) => ({ ...current, [currency]: amount }))}
                onBlur={() => saveCurrency(amounts)}
                outline
            />

            <p className="small text-body-secondary text-end mb-3">
                Total coin weight: <Weight value={totalCoinWeight} unit={weightUnit} />
            </p>

            <TableInventory
                className="inventory-table--treasure"
                thead={
                    <>
                        <th scope="col">
                            Name
                        </th>
                        <th scope="col" className="text-center" style={{ width: 100 }}>
                            <i className="fa fa-coins" aria-hidden="true" title="Value"></i>
                        </th>
                        <th scope="col" className="text-center" style={{ width: 100 }}>
                            <i className="fa fa-hashtag" aria-hidden="true" title="Quantity"></i>
                        </th>
                    </>
                }
                searchableItems={[]}
                showSearchableList={false}
                items={treasures}
                setItems={setTreasures}
                sorted
                onSelect={() => undefined}
                onSave={saveTreasure}
                onDelete={deleteTreasure}
                onNotesChange={(item, notes) => setTreasures(treasures.map((current) => current.id === item.id ? { ...current, notes } : current))}
                getWeight={(item) => item.weight}
                weightUnit={weightUnit}
                onWeightChange={(item, weight) => setTreasures(treasures.map((current) => current.id === item.id ? { ...current, weight } : current))}
                canEditWeight={(item) => item.treasure_id === null}
                getValue={(item) => item.value}
                onValueChange={(item, value) => setTreasures(treasures.map((current) => current.id === item.id ? { ...current, value } : current))}
                canEditValue={(item) => item.treasure_id === null}
                canSave={(item) => item.name.trim().length > 0}
                isUnsavedItem={(item) => typeof item.id === 'string'}
                columnCount={3}
                emptyMessage="No treasure. Keep searching for loot!"
                getKey={(item) => String(item.id)}
                getLabel={(item) => item.name}
                newItem={() => ({
                    id: `new-${nextPendingId.current++}`,
                    treasure_id: null,
                    name: '',
                    quantity: 1,
                    weight: 0,
                    notes: null,
                    value: emptyValue,
                })}
                renderItem={(item, isEditing) => <>
                    <td>
                        {isEditing ? (
                            <input
                                type="text"
                                value={item.name}
                                className="form-control"
                                onChange={(event) => setTreasures(treasures.map((current) => current.id === item.id ? { ...current, name: event.target.value } : current))}
                                aria-label="Custom treasure name" />
                        ) : item.name}
                    </td>

                    <td className="text-center">
                        <CurrencyText {...item.value} />
                    </td>

                    <td className="text-center">
                        {isEditing ? (
                            <NumberInput
                                min={1}
                                value={item.quantity}
                                className="form-control"
                                onValueChange={(quantity) => setTreasures(treasures.map((current) => current.id === item.id ? { ...current, quantity } : current))}
                                aria-label={`Quantity of ${item.name || 'custom treasure'}`} />
                        ) : item.quantity}
                    </td>

                </>}
            />
        </>
    );
}

export default Treasure;
